'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const CATEGORIES = [
  'Excavators',
  'Mini Excavators',
  'Wheeled Loaders',
  'Backhoe Loaders',
  'Bulldozers',
  'Dump Trucks',
  'Articulated Dump Trucks',
  'Crushers',
  'Screeners',
  'Cranes',
  'Telehandlers',
  'Compactors & Rollers',
  'Generators',
  'Concrete Equipment',
  'Drilling & Piling',
  'Demolition Equipment',
  'Attachments',
  'Trucks & Trailers',
  'Plant Hire Equipment',
  'Parts & Accessories',
  'Commercial Vehicle 4x4s',
  'Other',
];

interface BulkActionBarProps {
  count: number;
  onAction: (action: string) => void;
  onClear: () => void;
}

export default function BulkActionBar({ count, onAction, onClear }: BulkActionBarProps) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    }
    if (categoryOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryOpen]);

  return (
    <div className="bg-[var(--color-primary)] text-[var(--color-primary-foreground)] rounded-lg px-4 py-2.5 mb-3 flex items-center gap-3 flex-wrap">
      <span className="text-sm font-medium">{count} selected</span>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => onAction('active')} className="px-2.5 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors">Set Active</button>
        <button onClick={() => onAction('inactive')} className="px-2.5 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors">Set Inactive</button>
        <button onClick={() => onAction('pending')} className="px-2.5 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors">Set Pending</button>

        {/* Set Category dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setCategoryOpen(prev => !prev)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors"
          >
            <Icon name="TagIcon" size={12} variant="solid" />
            Set Category
            <Icon name="ChevronDownIcon" size={12} variant="outline" className={`transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
          </button>
          {categoryOpen && (
            <div className="absolute left-0 top-full mt-1 w-52 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    onAction(`category:${cat}`);
                    setCategoryOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        <button onClick={() => onAction('delete')} className="px-2.5 py-1 text-xs bg-red-500/80 hover:bg-red-500 rounded transition-colors flex items-center gap-1">
          <Icon name="TrashIcon" size={12} variant="solid" />
          Delete
        </button>
      </div>
      <button onClick={onClear} className="ml-auto p-1 hover:bg-white/20 rounded transition-colors" aria-label="Clear selection">
        <Icon name="XMarkIcon" size={16} variant="outline" />
      </button>
    </div>
  );
}